import React, { useState, useEffect } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { apiService } from '@/shared/services/apiService';
import { logger } from '@/shared/utils';
import { useConfirmDialog } from '@/shared/components/ui';
import toast from 'react-hot-toast';

interface PortfolioProject {
  id: string;
  title: string;
  description?: string;
  short_description?: string;
  project_url?: string;
  github_url?: string;
  status: 'active' | 'completed' | 'archived';
  featured: boolean;
  order_index: number;
  category?: string;
  created_at: string;
}

interface PortfolioProjectListResponse {
  projects: PortfolioProject[];
}

interface PortfolioFormState {
  title: string;
  short_description: string;
  project_url: string;
  github_url: string;
  status: 'active' | 'completed' | 'archived';
  featured: boolean;
}

const emptyForm: PortfolioFormState = {
  title: '',
  short_description: '',
  project_url: '',
  github_url: '',
  status: 'active',
  featured: false,
};

const AdminPortfolioPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PortfolioFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiService.get<PortfolioProjectListResponse>('/portfolio/projects');
      if (response.success && response.data) {
        setProjects(response.data.projects || []);
      } else {
        toast.error(response.error?.message || 'Failed to load portfolio projects');
        logger.error('Failed to load portfolio projects', 'AdminPortfolioPage', { error: response.error });
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Network error loading portfolio projects', 'AdminPortfolioPage', { error });
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEditForm = (project: PortfolioProject) => {
    setEditingId(project.id);
    setForm({
      title: project.title,
      short_description: project.short_description || '',
      project_url: project.project_url || '',
      github_url: project.github_url || '',
      status: project.status,
      featured: project.featured,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const response = editingId
        ? await apiService.put<PortfolioProject>(`/portfolio/projects/${editingId}`, form)
        : await apiService.post<PortfolioProject>('/portfolio/projects', form);

      if (response.success && response.data) {
        toast.success(editingId ? 'Project updated' : 'Project created');
        setShowForm(false);
        setForm(emptyForm);
        setEditingId(null);
        await loadProjects();
      } else {
        toast.error(response.error?.message || 'Failed to save project');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to save portfolio project', 'AdminPortfolioPage', { error });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (projectId: string) => {
    const ok = await confirm({
      title: 'Delete this project?',
      message: 'This permanently removes it and cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      requireTypedWord: 'DELETE',
    });
    if (!ok) return;

    try {
      const response = await apiService.delete(`/portfolio/projects/${projectId}`);
      if (response.success) {
        toast.success('Project deleted');
        await loadProjects();
      } else {
        toast.error(response.error?.message || 'Failed to delete project');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to delete portfolio project', 'AdminPortfolioPage', { error });
    }
  };

  const handleStatusChange = async (projectId: string, status: PortfolioProject['status']) => {
    try {
      const response = await apiService.put(`/portfolio/projects/${projectId}`, { status });
      if (response.success) {
        toast.success('Status updated');
        await loadProjects();
      } else {
        toast.error(response.error?.message || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to update portfolio project status', 'AdminPortfolioPage', { error });
    }
  };

  if (loading) {
    return (
      <div className={`${theme.card} p-8 text-center`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${theme.textAccent}`}></div>
        <p className={`mt-4 ${theme.textSecondary}`}>Loading portfolio projects...</p>
      </div>
    );
  }

  return (
    <div>
      <ConfirmDialog />
      <div className="flex justify-between items-center mb-6">
        <h2 className={`text-2xl font-bold ${theme.textHeading}`}>Portfolio Management</h2>
        <button
          onClick={openCreateForm}
          className={`px-4 py-2 rounded-xl transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
        >
          + New Project
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className={`${theme.card} rounded-3xl p-6 mb-6 space-y-4`}>
          <h3 className={`text-xl font-semibold ${theme.textHeading}`}>
            {editingId ? 'Edit Project' : 'New Project'}
          </h3>
          <div>
            <label className={`block text-sm mb-1 ${theme.textSecondary}`}>Title</label>
            <input
              className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>
          <div>
            <label className={`block text-sm mb-1 ${theme.textSecondary}`}>Short Description</label>
            <textarea
              className={`w-full px-3 py-2 rounded-lg ${theme.textarea} ${theme.textPrimary}`}
              value={form.short_description}
              onChange={(e) => setForm({ ...form, short_description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm mb-1 ${theme.textSecondary}`}>Project URL</label>
              <input
                className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
                value={form.project_url}
                onChange={(e) => setForm({ ...form, project_url: e.target.value })}
              />
            </div>
            <div>
              <label className={`block text-sm mb-1 ${theme.textSecondary}`}>GitHub URL</label>
              <input
                className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
                value={form.github_url}
                onChange={(e) => setForm({ ...form, github_url: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div>
              <label className={`block text-sm mb-1 ${theme.textSecondary}`}>Status</label>
              <select
                className={`px-3 py-2 rounded-lg ${theme.select} ${theme.textPrimary}`}
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as PortfolioFormState['status'] })}
              >
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <label className={`flex items-center gap-2 mt-5 ${theme.textSecondary}`}>
              <input
                type="checkbox"
                className={theme.checkbox}
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
              />
              Featured
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className={`px-4 py-2 rounded-xl transition ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className={`px-4 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className={`${theme.card} rounded-3xl p-6`}>
        <h3 className={`text-xl font-semibold mb-4 ${theme.textHeading}`}>All Projects</h3>

        {projects.length === 0 ? (
          <p className={`text-center py-8 ${theme.textMuted}`}>No portfolio projects yet</p>
        ) : (
          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className={`p-4 rounded-lg border border-opacity-10 transition-all duration-300 ${theme.textPrimary}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className={`font-semibold text-lg mb-1 ${theme.textHeading}`}>
                      {project.title} {project.featured && <span className={theme.textAccent}>★</span>}
                    </h4>
                    {project.short_description && (
                      <p className={`text-sm mb-2 ${theme.textSecondary}`}>{project.short_description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs">
                      <span className={theme.textMuted}>
                        Status: <span className={theme.textSecondary}>{project.status}</span>
                      </span>
                      {project.category && (
                        <span className={theme.textMuted}>
                          Category: <span className={theme.textSecondary}>{project.category}</span>
                        </span>
                      )}
                      {project.project_url && (
                        <a
                          href={project.project_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline ${theme.textAccent}`}
                        >
                          Live Site
                        </a>
                      )}
                      {project.github_url && (
                        <a
                          href={project.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`underline ${theme.textAccent}`}
                        >
                          GitHub
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4 flex-wrap justify-end">
                    <select
                      className={`px-3 py-2 rounded-lg text-sm ${theme.select} ${theme.textPrimary}`}
                      value={project.status}
                      onChange={(e) => handleStatusChange(project.id, e.target.value as PortfolioProject['status'])}
                    >
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="archived">Archived</option>
                    </select>
                    <button
                      onClick={() => openEditForm(project)}
                      className={`px-3 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
                    >
                      <span className={theme.textSecondary}>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className={`px-3 py-2 rounded-xl transition ${theme.buttonSecondary} ${theme.buttonTextSecondary}`}
                    >
                      <span className={theme.textError}>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPortfolioPage;
